import type { Properties } from 'posthog-js';
import { useEffect, useRef } from 'react';

import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';

// One sample per minute; the 2-minute idle cutoff lets a user read between clicks.
const HEARTBEAT_INTERVAL_MS = 60_000;
const IDLE_TIMEOUT_MS = 2 * 60_000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'wheel'] as const;
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

export default function useActiveHeartbeat(properties: Properties) {
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    let lastInteractionAt: number | null = null;

    const recordInteraction = () => {
      lastInteractionAt = Date.now();
    };

    const clearInteractionWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        lastInteractionAt = null;
      }
    };

    const sendHeartbeatIfActive = () => {
      const isPageVisible = document.visibilityState === 'visible';
      const isRecentlyActive =
        lastInteractionAt !== null && Date.now() - lastInteractionAt < IDLE_TIMEOUT_MS;

      if (isPageVisible && isRecentlyActive) {
        AnalyticsController.track('dispatch_active_heartbeat', propertiesRef.current);
      }
    };

    INTERACTION_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, recordInteraction, LISTENER_OPTIONS),
    );
    document.addEventListener('visibilitychange', clearInteractionWhenHidden);
    const intervalId = window.setInterval(sendHeartbeatIfActive, HEARTBEAT_INTERVAL_MS);

    return () => {
      INTERACTION_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, recordInteraction, LISTENER_OPTIONS),
      );
      document.removeEventListener('visibilitychange', clearInteractionWhenHidden);
      window.clearInterval(intervalId);
    };
  }, []);
}
